import React, { useState, useMemo } from "react";
import { Collapse, ListItemIcon, ListItemText, MenuItem, MenuList } from "@mui/material";
import { CaretDown, CaretRight, Flask } from "@phosphor-icons/react";
import { useEnabledExperimentFlags } from "../../hooks/useEnabledExperimentFlags";
import { useExperimentsManifest } from "../../hooks/useExperimentsManifest";
import {
  getEnabledExperimentsForAudience,
  type ExperimentAudience,
} from "../Experiments/experimentsManifest";

interface Props {
  userId: string | number | undefined;
  audience: ExperimentAudience;
  /** Called with the experiment id so the parent drawer can close itself, etc. */
  onNavigate?: (experimentId: string) => void;
}

/**
 * Drawer entry for the Experiments group. Renders nothing when the user has
 * no enabled `exp_iso_*` flags. Otherwise renders an expandable section with
 * one sub-item per enabled experiment for the given audience.
 */
export const ExperimentsDrawerGroup: React.FC<Props> = ({ userId, audience, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const { flags } = useEnabledExperimentFlags(userId);
  const { manifest } = useExperimentsManifest();

  const items = useMemo(
    () => getEnabledExperimentsForAudience(manifest, flags, audience),
    [manifest, flags, audience],
  );

  if (items.length === 0) {
    return null;
  }

  const labBaseUrl = process.env.LAB_BASE_URL;

  const handleSelect = (exp: { id: string; launchPath: string }) => {
    if (onNavigate) onNavigate(exp.id);
    if (!labBaseUrl) {
      console.warn('[ExperimentsDrawerGroup] LAB_BASE_URL not configured — cannot open experiment');
      return;
    }
    window.open(`${labBaseUrl}${exp.launchPath}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <MenuItem
        onClick={() => setOpen((o) => !o)}
        sx={{
          px: "24px",
          color: "#333",
          "&:hover": { backgroundColor: "#f5f5f5" },
        }}
      >
        <ListItemIcon sx={{ minWidth: "28px" }}>
          <Flask size={20} color="#798397" weight="duotone" />
        </ListItemIcon>
        <ListItemText primary="Experiments" />
        {open ? (
          <CaretDown size={16} color="#31333A" />
        ) : (
          <CaretRight size={16} color="#31333A" />
        )}
      </MenuItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <MenuList sx={{ padding: "0px" }}>
          {items.map((exp) => (
            <MenuItem
              key={exp.id}
              onClick={() => handleSelect(exp)}
              sx={{
                pl: "60px",
                pr: "24px",
                color: "#333",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <ListItemText primary={exp.title} />
            </MenuItem>
          ))}
        </MenuList>
      </Collapse>
    </>
  );
};
